import type { z } from 'zod';
import { PRESETS } from './index';
import { PRESET_CONFIG_CONTROLS, type PresetConfigControl } from './controls';

export type PresetFieldType = 'text' | 'number';

/** One editable config field the admin form renders for a preset. */
export type PresetConfigField = {
  /** Property name on the preset's config object. */
  key: string;
  /** Human label derived from the key. */
  label: string;
  type: PresetFieldType;
  required: boolean;
  integer: boolean;
  min?: number;
  max?: number;
  /** Text of the longest `min` check (used as a placeholder/length hint). */
  minLength?: number;
  /** Richer control to render instead of a plain input (A5b.6); absent = plain. */
  control?: PresetConfigControl;
};

function labelFromKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function checks(zodType: z.ZodTypeAny): Array<{ kind: string; value?: number }> {
  const checks = (zodType._def as { checks?: Array<{ kind: string; value?: number }> }).checks ?? [];
  return checks;
}

function numberConstraint(zodType: z.ZodTypeAny, kind: 'min' | 'max'): number | undefined {
  const match = checks(zodType).find((check) => check.kind === kind);
  return match?.value;
}

function integerFlag(zodType: z.ZodTypeAny): boolean {
  return checks(zodType).some((check) => check.kind === 'int');
}

/**
 * Derives the editable config fields for a preset straight from its Zod schema
 * (BUILD.md §4: the form is schema-driven, so the client never duplicates the
 * preset's validation). Supports the primitive field types the wave-1 presets
 * use (string / integer / number, optionally optional). Returning an empty list
 * means "no generated form" — the caller still accepts free-form JSON, which the
 * server validates against the same schema at publish time.
 */
export function presetConfigFields(presetKey: string): PresetConfigField[] {
  const preset = PRESETS.find((candidate) => candidate.key === presetKey);
  if (!preset) return [];
  const schema = preset.config_schema as z.ZodObject<Record<string, z.ZodTypeAny>>;
  const shape = schema.shape ?? {};
  const fields: PresetConfigField[] = [];
  for (const [key, fieldSchema] of Object.entries(shape)) {
    const optional = fieldSchema.isOptional();
    const inner: z.ZodTypeAny = optional ? (fieldSchema._def as { innerType: z.ZodTypeAny }).innerType : fieldSchema;
    const typeName = (inner._def as { typeName?: string }).typeName ?? '';
    const control = PRESET_CONFIG_CONTROLS[`${presetKey}.${key}`];
    let type: PresetFieldType;
    if (typeName === 'ZodNumber') type = 'number';
    else if (typeName === 'ZodString') type = 'text';
    else if (control) type = 'text'; // e.g. an array edited through a richer control (A5b.7)
    else continue; // unsupported shape (array/object/enum) with no control — not rendered
    fields.push({
      key,
      label: labelFromKey(key),
      type,
      required: !optional,
      integer: type === 'number' && integerFlag(inner),
      min: type === 'number' ? numberConstraint(inner, 'min') : undefined,
      max: type === 'number' ? numberConstraint(inner, 'max') : undefined,
      minLength: type === 'text' && typeName === 'ZodString' ? numberConstraint(inner, 'min') : undefined,
      control
    });
  }
  return fields;
}
